import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { useIsMobile } from "@/hooks/use-mobile";
import { buildListingWebUrl } from "@/lib/native/parseAppUrl";
import { openListingInNativeApp } from "@/lib/native/openInAppLink";
import { useLanguage } from "@/context/LanguageContext";
import { nativeAppLabels } from "@/i18n/labels/commonLabels";

/** Set to true when the native app is live in Play Store / App Store. */
const OPEN_IN_APP_BANNER_ENABLED = false;

function dismissKey(campaignId: string, listingId: string): string {
  return `bilenia_open_in_app_dismissed:${campaignId}:${listingId}`;
}

/** Collapsed mobile bidding bar height — banner stacks just above this on mobile web. */
export const MOBILE_WEB_BIDDING_BAR_OFFSET =
  "calc(5.5rem + env(safe-area-inset-bottom, 0px))";

type OpenInAppBannerProps = {
  campaignId: string;
  listingId: string;
  /** Sit above fixed MobileBiddingBar instead of covering it. */
  stackAboveBiddingBar?: boolean;
};

/**
 * Mobile web banner — deep link to native app when installed, else store.
 */
export function OpenInAppBanner({
  campaignId,
  listingId,
  stackAboveBiddingBar = false,
}: OpenInAppBannerProps) {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const t = nativeAppLabels(language);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(
        sessionStorage.getItem(dismissKey(campaignId, listingId)) === "1",
      );
    } catch {
      setDismissed(false);
    }
  }, [campaignId, listingId]);

  if (!OPEN_IN_APP_BANNER_ENABLED || isNative || !isMobile || dismissed) return null;

  const appUrl = buildListingWebUrl(campaignId, listingId);

  return (
    <div
      className="fixed left-0 right-0 z-[80] flex items-center gap-2 border-t border-border bg-card/95 backdrop-blur px-3 py-2.5 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
      style={{
        bottom: stackAboveBiddingBar ? MOBILE_WEB_BIDDING_BAR_OFFSET : 0,
        paddingBottom: stackAboveBiddingBar
          ? "0.625rem"
          : "max(0.625rem, env(safe-area-inset-bottom))",
      }}
    >
      <Button
        type="button"
        size="sm"
        className="flex-1 shrink min-w-0"
        onClick={() => openListingInNativeApp(appUrl)}
      >
        {t.openInApp}
      </Button>
      <button
        type="button"
        className="rounded-full p-1.5 text-muted-foreground hover:bg-muted"
        aria-label={t.continueInBrowser}
        onClick={() => {
          try {
            sessionStorage.setItem(dismissKey(campaignId, listingId), "1");
          } catch {
            /* ignore */
          }
          setDismissed(true);
        }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
